import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import extensionBridge from '../services/extensionBridge';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as RepeatIcon } from '../assets/icons/repeat.svg';
import { ReactComponent as CheckIcon } from '../assets/icons/check.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as AlertIcon } from '../assets/icons/alert-circle.svg';
import { ReactComponent as XIcon } from '../assets/icons/x.svg';
import './ExtensionUpdate.css';

// Compares "a.b.c" version strings part by part. >0 if a is newer than b.
export function compareVersions(a, b) {
  const partsA = String(a).split('.').map(Number);
  const partsB = String(b).split('.').map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function ExtensionUpdate({ onClose, onStatusChange }) {
  const { t } = useTranslation();
  const [latestInfo, setLatestInfo] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [installedVersion, setInstalledVersion] = useState(undefined); // undefined = checking
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch('/downloads/extension.json', { cache: 'no-store' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!cancelled) setLatestInfo(data);
      })
      .catch(() => {
        if (!cancelled) setLatestInfo(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingLatest(false);
      });

    extensionBridge.getInstalledExtensionVersion().then(version => {
      if (!cancelled) setInstalledVersion(version);
    });

    return () => { cancelled = true; };
  }, []);

  const hasUpdate = !!(latestInfo?.version && installedVersion &&
    compareVersions(latestInfo.version, installedVersion) > 0);

  // Let the caller show a discreet dot on the header button once we know.
  useEffect(() => {
    if (installedVersion !== undefined && latestInfo) {
      onStatusChange?.(hasUpdate);
    }
  }, [installedVersion, latestInfo, hasUpdate, onStatusChange]);

  const handleDownload = useCallback(() => {
    if (latestInfo?.url) {
      window.open(latestInfo.url, '_blank', 'noopener,noreferrer');
    }
  }, [latestInfo]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText('chrome://extensions').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, []);

  const installedLabel = installedVersion === undefined
    ? t('extension.checking')
    : (installedVersion || t('extension.notDetected'));

  return (
    <div className="extension-update-overlay" onClick={onClose}>
      <div className="extension-update-modal" onClick={(e) => e.stopPropagation()}>
        <div className="eu-header">
          <h2>{t('extension.title')}</h2>
          <button className="eu-close-btn" onClick={onClose}>
            <XIcon className="eu-icon" />
          </button>
        </div>

        <div className="eu-content">
          <div className="eu-row">
            <span className="eu-label">{t('extension.latestVersion')}</span>
            <span className="eu-value">
              {loadingLatest ? t('extension.checking') : (latestInfo?.version || '—')}
            </span>
          </div>
          <div className="eu-row">
            <span className="eu-label">{t('extension.installedVersion')}</span>
            <span className="eu-value">
              {installedVersion === null && <AlertIcon className="eu-icon eu-icon-warn" />}
              {installedLabel}
            </span>
          </div>

          {installedVersion === null && (
            <button className="eu-btn-primary" onClick={handleDownload} disabled={!latestInfo?.url}>
              <DownloadIcon className="eu-icon" />
              {t('extension.download')}
            </button>
          )}

          {installedVersion && !hasUpdate && (
            <div className="eu-status eu-status-ok">
              <CheckIcon className="eu-icon" />
              {t('extension.upToDate')}
            </div>
          )}

          {installedVersion && hasUpdate && (
            <button className="eu-btn-primary" onClick={handleDownload}>
              <RepeatIcon className="eu-icon" />
              {t('extension.updateTo', { version: latestInfo.version })}
            </button>
          )}

          <div className="eu-instructions">
            <h3>{t('extension.howToInstall')}</h3>
            <ol>
              <li>{t('extension.step1')}</li>
              <li>{t('extension.step2')}</li>
              <li>{t('extension.step3')}</li>
            </ol>
            <button className="eu-copy-btn" onClick={handleCopyLink}>
              {copied ? <CheckIcon className="eu-icon" /> : <CopyIcon className="eu-icon" />}
              {copied ? t('extension.copied') : t('extension.copyLink')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExtensionUpdate;
